/**
 * "Blood tests" comparison layout for Step 1 of the booking flow.
 *
 * Two side-by-side cards shown on the blood-only tab:
 *
 *   ┌───────────────────────────┐  ┌───────────────────────────┐
 *   │ Ahead membership          │  │ Single blood test         │
 *   │ (lavender, recommended)   │  │ (white, the one-off option)│
 *   │  MembershipCard           │  │  BaselineCard             │
 *   └───────────────────────────┘  └───────────────────────────┘
 *
 * Cards stack on mobile and sit in an equal-width, centered row on lg+.
 */
import { Product } from "./types";
import { pickBaselineProduct } from "./pickBaselineProduct";
import { BookingLanguage } from "@/types/booking";
import { languageMap } from "@/i18n";
import { membershipSignupUrl } from "@/utils/prefetchMembership";
import { MembershipCard, BaselineCard } from "./BloodTrackingCards";
import { MembershipTier } from "@/hooks/createProductsQueryFn";

interface BloodTrackingLayoutProps {
  products: Product[];
  selectedPackageId: string | null;
  onSelectPackage: (id: string) => void;
  language: BookingLanguage;
  onSelectAndContinue?: (id: string) => void;
  membershipTiers?: MembershipTier[];
  /** When set, the membership CTA stays in-flow (next page) instead of opening
   *  the external signup. */
  onStartMembership?: () => void;
}

export const BloodTrackingLayout = ({
  products,
  selectedPackageId: _selectedPackageId,
  onSelectPackage,
  language,
  onSelectAndContinue,
  membershipTiers,
  onStartMembership,
}: BloodTrackingLayoutProps) => {
  const baseline = pickBaselineProduct(products);

  // Membership signup: in-flow next page when a handler is provided, otherwise
  // fall back to the external marketing-site signup.
  const handleStartMembership = () => {
    if (onStartMembership) {
      onStartMembership();
      return;
    }
    const locale = languageMap[language] ?? "en";
    window.open(membershipSignupUrl(locale), "_blank");
  };

  // The one-off card selects the baseline blood test and advances the flow.
  const handleSelectBaseline = () => {
    if (!baseline) return;
    if (onSelectAndContinue) onSelectAndContinue(baseline.id);
    else onSelectPackage(baseline.id);
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Mobile (< lg): cards stack — the one-off drops below the membership
          card. lg+: equal-width, centered row, each capped at 400px (440 on
          tablet). */}
      <div className="flex flex-col gap-4 lg:flex-row lg:items-stretch lg:justify-center">
        <div className="w-full mx-auto max-w-[400px] md:max-w-[440px] lg:mx-0 lg:max-w-[400px] lg:flex-1">
          <MembershipCard
            onStart={handleStartMembership}
            infoProduct={baseline}
            language={language}
            membershipTiers={membershipTiers}
          />
        </div>
        {baseline && (
          <div className="w-full mx-auto max-w-[400px] md:max-w-[440px] lg:mx-0 lg:max-w-[400px] lg:flex-1">
            <BaselineCard
              onSelect={handleSelectBaseline}
              infoProduct={baseline}
              language={language}
            />
          </div>
        )}
      </div>
    </div>
  );
};
